/**
 * Sidebar Component
 * Phase 2: Product Shell & Navigation
 * 
 * Persistent left sidebar with grouped navigation, collapsible behavior,
 * and active state indication following HMVM design conventions.
 */

import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Icon } from "./Icon";
import { NAVIGATION_GROUPS, isRouteActive, type RouteConfig } from "../lib/navigation";
import { useHealthCheck } from "../hooks/useHealthCheck";
import { useLocale } from "../contexts/LocaleContext";

interface SidebarProps {
  collapsed: boolean;
  onToggleCollapsed: () => void;
}

export function Sidebar({ collapsed, onToggleCollapsed }: SidebarProps) {
  const location = useLocation();
  const { health } = useHealthCheck();
  const { t } = useLocale();

  return (
    <aside
      className="sidebar"
      data-collapsed={collapsed}
      style={{
        width: collapsed ? "64px" : "240px",
        borderRight: "1px solid var(--reviewer-border)",
        backgroundColor: "var(--reviewer-bg-primary)",
        display: "flex",
        flexDirection: "column",
        transition: "width 0.2s ease",
      }}
    >
      {/* Sidebar Header */}
      <div
        className="sidebar-header"
        style={{
          padding: collapsed ? "16px 12px" : "16px 20px",
          borderBottom: "1px solid var(--reviewer-border)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          minHeight: "64px",
        }}
      >
        <button
          onClick={onToggleCollapsed}
          className="sidebar-collapse-btn"
          aria-label={collapsed ? t("common.expandSidebar") : t("common.collapseSidebar")}
          style={{
            background: "none",
            border: "none",
            padding: "8px",
            cursor: "pointer",
            color: "var(--reviewer-text-secondary)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "0",
            transition: "color 0.2s ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = "var(--reviewer-text-primary)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = "var(--reviewer-text-secondary)";
          }}
        >
          <Icon name="menu" size={20} />
        </button>
      </div>

      {/* Navigation Groups */}
      <nav
        className="sidebar-nav"
        style={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          padding: collapsed ? "12px 8px" : "16px 12px",
        }}
      >
        {NAVIGATION_GROUPS.map((group, groupIndex) => (
          <div
            key={group.id}
            className="sidebar-nav-group"
            style={{
              marginBottom: groupIndex < NAVIGATION_GROUPS.length - 1 ? "24px" : "0",
            }}
          >
            {/* Group Label */}
            {!collapsed && (
              <div
                className="sidebar-nav-group-label"
                style={{
                  fontSize: "11px",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  color: "var(--reviewer-text-tertiary)",
                  marginBottom: "8px",
                  paddingLeft: "12px",
                  fontFamily: "var(--reviewer-font-mono)",
                }}
              >
                {t(group.labelKey)}
              </div>
            )}

            {/* Group Routes */}
            <ul
              style={{
                listStyle: "none",
                padding: 0,
                margin: 0,
                display: "flex",
                flexDirection: "column",
                gap: "2px",
              }}
            >
              {group.routes.map((route) => (
                <SidebarNavItem
                  key={route.path}
                  route={route}
                  collapsed={collapsed}
                  isActive={isRouteActive(route.path, location.pathname)}
                />
              ))}
            </ul>
          </div>
        ))}
      </nav>

      {/* Sidebar Footer (optional info) */}
      {!collapsed && (
        <div
          className="sidebar-footer"
          style={{
            padding: "12px 16px",
            borderTop: "1px solid var(--reviewer-border)",
            fontSize: "10px",
            color: "var(--reviewer-text-tertiary)",
            fontFamily: "var(--reviewer-font-mono)",
          }}
        >
          {health?.version ? `v${health.version}` : ""}
        </div>
      )}
    </aside>
  );
}

// ============================================================================
// Sidebar Nav Item Component
// ============================================================================

interface SidebarNavItemProps {
  route: RouteConfig;
  collapsed: boolean;
  isActive: boolean;
}

function SidebarNavItem({ route, collapsed, isActive }: SidebarNavItemProps) {
  const [isHovered, setIsHovered] = useState(false);
  const { t } = useLocale();
  const linkPath = route.path.includes(":") ? route.parentPath || route.path : route.path;
  const label = t(route.labelKey);

  return (
    <li>
      <Link
        to={linkPath}
        data-testid={`nav-${route.label.toLowerCase().replace(/\s+/g, "-")}`}
        style={{ textDecoration: "none", display: "block" }}
      >
        <div
          className="sidebar-nav-item"
          style={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            gap: collapsed ? "0" : "12px",
            padding: collapsed ? "12px" : "10px 12px",
            justifyContent: collapsed ? "center" : "flex-start",
            borderRadius: "0",
            color: isActive
              ? "var(--reviewer-text-primary)"
              : "var(--reviewer-text-secondary)",
            backgroundColor: isActive
              ? "var(--reviewer-bg-secondary)"
              : isHovered
              ? "var(--reviewer-bg-secondary)"
              : "transparent",
            cursor: "pointer",
            transition: "background-color 0.15s ease, color 0.15s ease",
            fontFamily: "var(--reviewer-font-mono)",
            fontSize: "14px",
            fontWeight: isActive ? 500 : 400,
            textDecoration: "none",
            borderLeft: isActive ? "3px solid var(--reviewer-accent)" : "3px solid transparent",
            marginLeft: collapsed ? "0" : "-3px",
          }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          title={collapsed ? label : undefined}
          aria-label={label}
        >
          <Icon name={route.icon as any} size={20} />
          {!collapsed && (
            <span style={{ flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {label}
            </span>
          )}
        </div>
      </Link>
    </li>
  );
}
